import { Row, Col, Form, Button, Card, Image } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import Select from 'components/general/ViewportSelect';
import { Link } from 'react-router-dom';
import { canManagePageBuilder, isMemberEquivalent } from 'utils/acl';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';
import DebateAssignments from './DebateAssignments';

const assignmentStatusClass = (confirmed) => {
  if (confirmed === true) return 'confirmed'
  if (confirmed === false) return 'rejected'
  return 'waiting'
}

const assignmentStatusText = (confirmed) => {
  if (confirmed === true) return 'Confirmed'
  if (confirmed === false) return 'Rejected'
  return 'Awaiting response'
}

const getPresenterAvatarSrc = (user) => resolveAvatarSrc(user?.avatarUrl || user?.avatar || '')

export default function AssignmentsCard({ getRoleAssignment, getMeeting, assignments, agenda = [], meeting }) {
  // Track theme for live Select style updates
  const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light');

  useEffect(() => {
    // Listen for changes to body[data-theme]
    const observer = new MutationObserver(() => {
      setTheme(document.body.getAttribute('data-theme') || 'light');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  const { user: authUser } = useSelector(x => x.auth)
  const params = useParams()

  const url = process.env.REACT_APP_URL

  // console.log('assignments', assignments)

  const [isLoading, setIsLoading] = useState(true)
  const [presenters, setPresenters] = useState({ officersAndMembers: [], guests: [] })
  const [selectedPresentersByRole, setSelectedPresentersByRole] = useState({})
  const [editingPresenterRoleId, setEditingPresenterRoleId] = useState('')
  const [loadingAssignment, setLoadingAssignment] = useState({}); // assignmentId: bool
  const [specialAssignments, setSpecialAssignments] = useState({ timerManager: null, pollManager: null })
  const [loadingSpecialAssignment, setLoadingSpecialAssignment] = useState({})

  const [presenterAvatars, setPresenterAvatars] = useState({})

  const meetingId = params.meetingId
  const canManageAssignments = canManagePageBuilder(authUser)

  useEffect(() => {
    const meetingSpecialAssignments = meeting?.meetingDetails?.specialAssignments || {}
    setSpecialAssignments({
      timerManager: String(meetingSpecialAssignments.timerManager || '').trim() || null,
      pollManager: String(meetingSpecialAssignments.pollManager || '').trim() || null,
    })
  }, [meeting?.meetingDetails?.specialAssignments, meeting?.meetingDetails?.specialAssignments?.timerManager, meeting?.meetingDetails?.specialAssignments?.pollManager])

  const getPresenters = async () => {
    const response = await fetch(`${url}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authUser.token}`
      }
    })
    if (response.status === 200) {
      const presenters = await response.json()
      // Group presenters by role
      const officersAndMembers = presenters.filter(p => isMemberEquivalent(p.acl)).sort((a, b) => a.firstName.localeCompare(b.firstName))
      const guests = presenters.filter(p => p.acl === 'Guest').sort((a, b) => a.firstName.localeCompare(b.firstName))
      setPresenters({ officersAndMembers, guests })
    }
    else {
      console.log(response)
    }
  }



  const savePresenterForRole = async (roleId, presenterId) => {
    const assignment = (Array.isArray(assignments) ? assignments : [])
      .find((item) => String(item?.role?._id || '') === String(roleId || ''))
    const selectedPresenterId = String(presenterId ?? '').trim()

    if (!selectedPresenterId) {
      if (!assignment?._id || !assignment.user) return

      const busyKey = String(roleId || '')
      setLoadingAssignment(prev => ({ ...prev, [busyKey]: true }))
      try {
        const response = await fetch(`${url}/roleAssignment/${assignment._id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + authUser.token,
          },
          body: JSON.stringify({ user: null }),
        })

        if (!response.ok) throw new Error('Failed to remove presenter')
        await getRoleAssignment()
        await getMeeting()
      } catch (error) {
        toast.error(error.message || 'Unable to remove presenter', { position: 'bottom-left' })
      } finally {
        setLoadingAssignment(prev => ({ ...prev, [busyKey]: false }))
      }
      return
    }

    if (String(assignment?.user?._id || '') === selectedPresenterId) {
      return
    }

    const busyKey = String(roleId || '')
    setLoadingAssignment(prev => ({ ...prev, [busyKey]: true }))

    try {
      if (assignment?._id) {
        const response = await fetch(`${url}/roleAssignment/${assignment._id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`
          },
          body: JSON.stringify({ user: selectedPresenterId }),
        })

        if (!response.ok) throw new Error('Failed to update presenter')
      } else {
        const response = await fetch(`${url}/roleAssignment/${meetingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`
          },
          body: JSON.stringify({
            role: roleId,
            user: selectedPresenterId
          })
        })

        if (!response.ok) throw new Error('Failed to create role assignment')
      }

      await getRoleAssignment()
      await getMeeting()
    }
    catch (error) {
      toast.error(error.message || 'Unable to save presenter', { position: 'bottom-left' })
    }
    finally {
      setLoadingAssignment(prev => ({ ...prev, [busyKey]: false }))
    }
  }

  const updateSpecialAssignment = async (field, userId) => {
    if (!meetingId) return

    const currentValue = String(specialAssignments?.[field] || '').trim() || null
    const selectedUserId = String(userId || '').trim() || null
    const nextValue = currentValue === selectedUserId ? null : selectedUserId
    const nextSpecialAssignments = {
      timerManager: field === 'timerManager' ? nextValue : String(specialAssignments?.timerManager || '').trim() || null,
      pollManager: field === 'pollManager' ? nextValue : String(specialAssignments?.pollManager || '').trim() || null,
    }

    setSpecialAssignments(nextSpecialAssignments)
    setLoadingSpecialAssignment(prev => ({ ...prev, [field]: true }))

    try {
      const response = await fetch(`${url}/meeting/${meetingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`
        },
        body: JSON.stringify({ specialAssignments: nextSpecialAssignments })
      })

      if (!response.ok) {
        throw new Error('Failed to update special assignment')
      }

      await Promise.all([getMeeting(), getRoleAssignment()])
    } catch (error) {
      setSpecialAssignments({
        timerManager: String(meeting?.meetingDetails?.specialAssignments?.timerManager || '').trim() || null,
        pollManager: String(meeting?.meetingDetails?.specialAssignments?.pollManager || '').trim() || null,
      })
      toast.error(error.message || 'Unable to update special assignment', { position: 'bottom-left' })
    } finally {
      setLoadingSpecialAssignment(prev => ({ ...prev, [field]: false }))
    }
  }

  // Fix: Always get assignmentId from argument, not from event.target.name
  const removeAssignment = async (e, assignmentId) => {
    e.preventDefault();
    if (!assignmentId) {
      toast.error('Assignment ID is missing', { position: 'bottom-left' });
      return;
    }
    console.log('Removing assignment with ID:', assignmentId);
    const response = await fetch(`${url}/roleAssignment/${assignmentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authUser.token}`
      },
    });
    if (response.status === 200) {
      await response.json();
      getRoleAssignment();
      getMeeting();
    } else {
      console.log(response);
      toast.error('Role assignment removal failed', { position: 'bottom-left' });
    }
  };

  // PATCH role assignment confirmation
  const patchAssignment = async (assignmentId, value) => {
    setLoadingAssignment(prev => ({ ...prev, [assignmentId]: true }));
    try {
      await fetch(`${url}/roleAssignment/${assignmentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}` // Assuming you have a token for auth
        },
        body: JSON.stringify({ confirmed: value }),
      });
      await getRoleAssignment(); // <-- This updates the assignments list
      await getMeeting();
    } catch (e) {
      console.error(e);
    }
    setLoadingAssignment(prev => ({ ...prev, [assignmentId]: false }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      await getRoleAssignment()
      setIsLoading(false)
    }
    fetchData()
    // eslint-disable-next-line
  }, [authUser, canManageAssignments])

  useEffect(() => {
    let mounted = true
    try {
      const usersToCheck = (Array.isArray(assignments) ? assignments : []).map(a => a?.user).filter(Boolean)
      usersToCheck.forEach((user) => {
        const key = String(user?.tmAuthUserId || user?._id || '').trim()
        if (!key) return
        const existing = getPresenterAvatarSrc(user)
        if (existing) return
        if (presenterAvatars[key]) return

        (async () => {
          try {
            const resp = await fetch(`${url}/unified/${encodeURIComponent(key)}`)
            if (!mounted) return
            if (!resp.ok) return
            const payload = await resp.json().catch(() => null)
            const avatar = payload?.identity?.avatarUrl || payload?.identity?.avatar || null
            const src = avatar ? resolveAvatarSrc(avatar) : null
            if (src && mounted) setPresenterAvatars(prev => ({ ...prev, [key]: src }))
          } catch (_err) {
            // ignore
          }
        })()
      })
    } catch (_err) {
      // ignore
    }

    return () => { mounted = false }
  }, [assignments, presenterAvatars, url])

  const presenterOptions = useMemo(
    () => [
      {
        label: 'Members',
        options: [...presenters.officersAndMembers]
          .sort((a, b) => `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`))
          .map(presenter => ({ value: presenter._id, label: `${presenter.firstName} ${presenter.lastName}` }))
      },
      {
        label: 'Guests',
        options: [...presenters.guests]
          .sort((a, b) => `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`))
          .map(presenter => ({ value: presenter._id, label: `${presenter.firstName} ${presenter.lastName}` }))
      }
    ],
    [presenters.officersAndMembers, presenters.guests]
  )

  const allPresenterOptions = useMemo(
    () => presenterOptions.flatMap(group => group.options || []),
    [presenterOptions]
  )

  const agendaRoles = useMemo(() => {
    const source = Array.isArray(agenda) ? agenda : []
    const unique = new Map()

    source.forEach((item) => {
      const roleId = String(item?.meetingRole?._id || '')
      const roleName = String(item?.meetingRole?.name || '').trim()
      if (!roleId || !roleName) return

      if (!unique.has(roleId)) {
        unique.set(roleId, {
          roleId,
          roleName,
          count: 1,
        })
      } else {
        const existing = unique.get(roleId)
        unique.set(roleId, {
          ...existing,
          count: existing.count + 1,
        })
      }
    })

    return Array.from(unique.values())
  }, [agenda])

  const assignmentsByRole = useMemo(() => {
    const map = new Map()
      ; (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
        const roleId = String(assignment?.role?._id || '')
        if (!roleId) return
        map.set(roleId, assignment)
      })
    return map
  }, [assignments])

  const canModerateAll = Boolean(
    canManageAssignments
    && (
      Boolean(authUser?.user?.isWebmaster)
      || (
        Array.isArray(authUser?.user?.clubRoles)
        && (authUser.user.clubRoles.includes('President')
          || authUser.user.clubRoles.includes('Vice President of Education'))
      )
    )
  )

  const isOfficer = Boolean(canManageAssignments)

  const canRespondToAssignment = (assignment) => {
    if (!assignment) return false
    return canModerateAll || String(authUser?.user?._id || '') === String(assignment?.user?._id || '')
  }

  // Theme-aware react-select styles using CSS variables
  const getSelectStyles = () => {
    const root = document.body;
    const computed = getComputedStyle(root);
    const bg = computed.getPropertyValue('--tm-light-bg') || '#fff';
    const darkBg = computed.getPropertyValue('--tm-dark-bg') || '#23272b';
    const fg = computed.getPropertyValue('--foreground') || '#222';
    const border = computed.getPropertyValue('--modal-borders') || '#ccc';
    const placeholder = computed.getPropertyValue('--tm-gray-text') || '#666';
    // Use theme from state (updates on theme switch)
    const controlBg = theme === 'dark' ? darkBg : bg;
    const menuBg = theme === 'dark' ? darkBg : bg;
    const textColor = theme === 'dark' ? '#fff' : fg;
    const optionSelected = theme === 'dark' ? '#495057' : '#e9ecef';
    const optionFocused = theme === 'dark' ? '#495057' : '#f1f3f5';
    return {
      control: (provided) => ({
        ...provided,
        backgroundColor: controlBg,
        color: textColor,
        borderColor: border,
      }),
      menu: (provided) => ({
        ...provided,
        position: 'static',
        marginTop: '0.35rem',
        backgroundColor: menuBg,
        color: textColor,
        border: `1px solid ${border}`,
        borderRadius: 8,
        boxShadow: theme === 'dark'
          ? '0 10px 28px rgba(0, 0, 0, 0.45)'
          : '0 10px 26px rgba(16, 24, 40, 0.14)',
        zIndex: 2,
      }),
      menuList: (provided) => ({
        ...provided,
        maxHeight: 180,
      }),
      singleValue: (provided) => ({
        ...provided,
        color: textColor,
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? optionSelected
          : state.isFocused
            ? optionFocused
            : menuBg,
        color: textColor,
      }),
      input: (provided) => ({
        ...provided,
        color: textColor,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: placeholder,
      }),
    };
  }


  return (
    <div className='assignments-modal-panel'>
      {!isLoading && agenda.filter(item => item.activity?.type === 'debate'
        || (item.agendaEvent?.eventType === 'pathway' && /debate/i.test(item.item_title || '')))
        .map(item => <DebateAssignments key={item.agendaItemId} item={item}
          users={[...presenters.officersAndMembers, ...presenters.guests]}
          meeting={meeting} getMeeting={getMeeting} canManage={canManageAssignments} />)}
      {isLoading ? (
        <div className='text-center py-4'>
          <AppLoader />
        </div>
      ) : agendaRoles.length === 0 ? (
        <p className='mb-0 profile-overview-empty-message'>No roles are currently used in this agenda.</p>
      ) : (
        <Row className='g-3'>
          {agendaRoles.map((agendaRole) => {
            const assignment = assignmentsByRole.get(agendaRole.roleId)
            const assignmentBusyKey = assignment?._id ? String(assignment._id) : String(agendaRole.roleId)
            const selectedPresenterId = selectedPresentersByRole[agendaRole.roleId]
              || String(assignment?.user?._id || '')
            const selectedPresenter = allPresenterOptions
              .find((option) => String(option.value) === String(selectedPresenterId)) || null
            const statusClass = assignmentStatusClass(assignment?.confirmed)
            const statusText = assignmentStatusText(assignment?.confirmed)
            const canRespond = canRespondToAssignment(assignment)
            const isPending = assignment?.confirmed === undefined || assignment?.confirmed === null
            const presenterAvatarSrc = getPresenterAvatarSrc(assignment?.user)
            const presenterInitials = getUserInitials(assignment?.user, 'T')
            const isEditingPresenter = editingPresenterRoleId === agendaRole.roleId
            const assignedUserId = String(assignment?.user?.tmAuthUserId || assignment?.user?._id || '')
            const presenterEffectiveSrc = presenterAvatarSrc || (assignedUserId ? presenterAvatars[assignedUserId] : null)
            const isTimerManager = Boolean(assignedUserId && String(specialAssignments.timerManager || '') === assignedUserId)
            const isPollManager = Boolean(assignedUserId && String(specialAssignments.pollManager || '') === assignedUserId)
            const canManageSpecialAssignments = isOfficer && Boolean(assignedUserId)

            return (
              <Col xs={12} md={6} key={`assignment-role-card-${agendaRole.roleId}`}>
                <Card className='assignments-modal-card meetings-mock-card-shell'>
                  <Card.Body>
                    <div className='assignments-modal-card-head'>
                      <div>
                        <h6 className='assignments-modal-role-name mb-0'>{agendaRole.roleName}</h6>
                        <span className='assignments-modal-role-count mt-1'>
                          {agendaRole.count} slot{agendaRole.count === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className='assignments-modal-card-head-actions'>
                        {assignment?._id && (
                          <span className={`assignments-history-status assignments-history-status--${statusClass}`}>
                            {statusText}
                          </span>
                        )}

                        {isOfficer && (
                          <div className='assignments-modal-special-toggle-group'>
                            <button
                              type='button'
                              className={`assignments-modal-special-toggle ${isTimerManager ? 'assignments-modal-special-toggle--active' : ''}`}
                              onClick={() => updateSpecialAssignment('timerManager', assignedUserId)}
                              disabled={!canManageSpecialAssignments || Boolean(loadingSpecialAssignment.timerManager)}
                              title={isTimerManager ? 'Clear timer manager' : 'Set timer manager'}
                              aria-label={isTimerManager ? 'Clear timer manager' : 'Set timer manager'}
                            >
                              <i className='bi bi-stopwatch' aria-hidden='true'></i>
                            </button>
                            <button
                              type='button'
                              className={`assignments-modal-special-toggle ${isPollManager ? 'assignments-modal-special-toggle--active' : ''}`}
                              onClick={() => updateSpecialAssignment('pollManager', assignedUserId)}
                              disabled={!canManageSpecialAssignments || Boolean(loadingSpecialAssignment.pollManager)}
                              title={isPollManager ? 'Clear poll manager' : 'Set poll manager'}
                              aria-label={isPollManager ? 'Clear poll manager' : 'Set poll manager'}
                            >
                              <i className='bi bi-bar-chart' aria-hidden='true'></i>
                            </button>
                          </div>
                        )}

                        {(isTimerManager || isPollManager) && (
                          <div className='assignments-modal-special-badges'>
                            {isTimerManager && <span className='assignments-history-status assignments-history-status--special'>Timer Manager</span>}
                            {isPollManager && <span className='assignments-history-status assignments-history-status--special'>Poll Manager</span>}
                          </div>
                        )}
                      </div>
                    </div>

                    {!isOfficer && (
                      <div className='assignments-modal-presenter-line'>
                        {assignment?.user?._id ? (
                          <>
                            <div className='assignments-modal-avatar'>
                                {presenterEffectiveSrc
                                  ? <Image src={presenterEffectiveSrc} alt='Presenter avatar' />
                                  : <span className='assignments-modal-avatar-fallback' aria-hidden='true'>{presenterInitials}</span>}
                            </div>
                            <Link to={`/profile/${assignment.user._id}`} className='profile-overview-card-link'>
                              {assignment.user.firstName} {assignment.user.lastName}
                            </Link>
                          </>
                        ) : (
                          <span className='assignments-modal-empty'>No presenter assigned yet.</span>
                        )}
                      </div>
                    )}

                    {isOfficer && !isEditingPresenter && (
                      <button
                        type='button'
                        className='assignments-modal-presenter-trigger'
                        onClick={() => {
                          setEditingPresenterRoleId(agendaRole.roleId)
                          getPresenters()
                        }}
                        disabled={Boolean(loadingAssignment[assignmentBusyKey])}
                      >
                        {assignment?.user?._id ? (
                          <>
                            <div className='assignments-modal-avatar'>
                              {presenterEffectiveSrc
                                ? <Image src={presenterEffectiveSrc} alt='Presenter avatar' />
                                : <span className='assignments-modal-avatar-fallback' aria-hidden='true'>{presenterInitials}</span>}
                            </div>
                            <span>{assignment.user.firstName} {assignment.user.lastName}</span>
                          </>
                        ) : (
                          <span className='assignments-modal-empty'>Select presenter</span>
                        )}
                      </button>
                    )}

                    {isOfficer && isEditingPresenter && (
                      <Form.Group className='mt-3'>
                        <Select
                          options={presenterOptions}
                          className='formInput'
                          name={`presenter-${agendaRole.roleId}`}
                          value={selectedPresenter}
                          onChange={(option) => {
                            const nextValue = option ? option.value : ''
                            setSelectedPresentersByRole((prev) => ({
                              ...prev,
                              [agendaRole.roleId]: nextValue
                            }))

                            if (nextValue) {
                              savePresenterForRole(agendaRole.roleId, nextValue)
                              setEditingPresenterRoleId('')
                            } else if (assignment?.user) {
                              savePresenterForRole(agendaRole.roleId, '')
                              setEditingPresenterRoleId('')
                            }
                          }}
                          isClearable
                          isSearchable
                          isDisabled={Boolean(loadingAssignment[assignmentBusyKey])}
                          styles={getSelectStyles()}
                          placeholder='Select a presenter'
                          onMenuOpen={getPresenters}
                          autoFocus
                        />
                        <div className='assignments-modal-inline-tools'>
                          <button
                            type='button'
                            className='assignments-modal-inline-cancel'
                            onClick={() => setEditingPresenterRoleId('')}
                          >
                            Cancel
                          </button>
                        </div>
                      </Form.Group>
                    )}

                    <div className='assignments-modal-actions'>
                      {assignment?._id && canRespond && (
                        isPending ? (
                          <>
                            <Button
                              type='button'
                              className='assignments-modal-btn assignments-modal-btn--danger'
                              disabled={loadingAssignment[assignmentBusyKey]}
                              onClick={() => patchAssignment(assignment._id, false)}
                            >
                              Decline
                            </Button>
                            <Button
                              type='button'
                              className='assignments-modal-btn assignments-modal-btn--success'
                              disabled={loadingAssignment[assignmentBusyKey]}
                              onClick={() => patchAssignment(assignment._id, true)}
                            >
                              Accept
                            </Button>
                          </>
                        ) : (
                          <Button
                            type='button'
                            className='assignments-modal-btn assignments-modal-btn--neutral'
                            disabled={loadingAssignment[assignmentBusyKey]}
                            onClick={() => patchAssignment(assignment._id, null)}
                          >
                            Reset
                          </Button>
                        )
                      )}

                      {assignment?._id && canModerateAll && (
                        <Button
                          type='button'
                          className='assignments-modal-btn assignments-modal-btn--danger-outline'
                          disabled={loadingAssignment[assignmentBusyKey]}
                          onClick={(e) => removeAssignment(e, assignment._id)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )
          })}
        </Row>
      )}
    </div>
  )
}
